import { DOCUMENT } from '@angular/common';
import {
	AfterViewChecked,
	ApplicationRef,
	ChangeDetectorRef,
	Component,
	ComponentFactoryResolver,
	EmbeddedViewRef,
	HostListener,
	Inject,
	Injector,
	NgZone,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaProgressReportService } from '../services/para-progress-report.service';
import { ParaOrgStateService } from '../services/para-org-state.service';
import { ParaRosterService } from '../services/para-roster.service';
import { ParaScoreSubmitService } from '../services/para-score-submit.service';
import { ParaLmsAssessmentService } from './para-lms-assessment.service';
import { ParaLmsDrawerContext } from '../models/para-drawer-context.type';
import {
	resolveStudentBadgeMilestoneOverlay,
	type ParaMilestoneWithStatus,
	type ParaStudentBadgeMilestoneOverlay,
} from '../progress-table/para-milestone.util';
import {
	getEffectiveNextChallenge,
	getChallengePositionInCatalog,
	getPhaseQuestionCatalog,
	getNextChallengeQuestionAfter,
	getPreviousChallengeQuestionBefore,
	lmsStoryUrlFromNextChallenge,
	mergeProgressRowChallangeHints,
	normalizeNextChallengeForLms,
} from './lms-assessment-lms.util';
import { debugParaLms } from './para-lms-assessment.debug';
import { normalizeChipColor } from '../utils/para-color.util';
import { PARA_SCORE_RUBRIC } from '../models/para-score-rubric.const';
import { formatChallengeDisplay } from '../utils/para-challenge-display.util';
import { LmsResumeStorageOverride } from './lms-resume-storage-override.util';
import { ParaScoreStorageService } from '../services/para-score-storage.service';
import { ParaSelectedAssessmentService } from '../services/para-selected-assessment.service';
import { ParaAssessmentSeriesService } from '../services/para-assessment-series.service';
import {
	ParaAssessmentContextService,
	type ParaAssessmentContextDto,
	type ParaLmsHeaderSupplementDto,
} from '../services/para-assessment-context.service';
import {
	buildLmsActiveSlideGuideLines,
	filterLmsHeaderSupplementsForActiveSlide,
	isFirstSlideOfLmsChallenge,
	normalizeLmsHeaderId,
	parseScoringGuideLineSegments,
	resolveLmsHeaderSupplementsForChallenge,
	type ParaScoreButton,
	type StoryScoreButtonProbe,
} from './lms-header-supplement.util';
import {
	canStartParaLmsAssessment,
	getParaLmsStartBlockReason,
	isParaScreenerStyleAssessment,
	isParaTransformStyleAssessment,
} from './para-lms-assessment-start.util';

/** Minimal Navigation API surface for reload intercept (see `setupPageReloadGuard`). */
interface ParaNavigationReloadEvent extends Event {
	canIntercept?: boolean;
	navigationType?: string;
	intercept?: (options: { handler: () => void | Promise<void> }) => void;
}

interface ParaWindowNavigation {
	addEventListener(type: 'navigate', listener: (event: Event) => void): void;
	removeEventListener(type: 'navigate', listener: (event: Event) => void): void;
}

@Component({
	selector: 'app-para-lms-assessment',
	templateUrl: './lms-assessment-slideout.component.html',
	styleUrls: ['./lms-assessment-slideout.component.css'],
})
export class LmsAssessmentSlideoutComponent implements OnInit, OnDestroy, AfterViewChecked {
	private static readonly STORY_HIDDEN_ACC_TEXTS = ['Door.png', 'Vector.svg'];

	private readonly destroy$ = new Subject<void>();
	@ViewChild('scoreReviewPortal') private scoreReviewPortal?: TemplateRef<unknown>;
	private scoreReviewPortalOutlet: DomPortalOutlet | null = null;
	private scoreReviewPortalRef: EmbeddedViewRef<unknown> | null = null;
	private scoreReviewPortalVisible = false;
	/**
	 * Storyline resume/suspend data uses `localStorage`. For a **same-origin** LMS URL (e.g.
	 * `/assets/LMS/...`), the iframe shares `localStorage` with the parent window — so we can
	 * block resume reads/writes from the parent frame without regenerating published `story.html`.
	 *
	 * **Cross-origin** LMS (different scheme/host/port) has its own storage; this override does
	 * not apply there.
	 */
	private readonly resumeStorageOverride = new LmsResumeStorageOverride({
		isResumeLikeValue: (value) => this.isStorylineResumeLikeValue(value),
		debug: (...args: any[]) => (debugParaLms as any)(...args),
	});

	public drawerState: 'closed' | 'assessing' | 'scoring' | 'confirmation' | 'submitting' | 'done' | 'confirmClose' = 'closed';

	/** Why the discard-unsaved-scores modal is shown (close drawer vs page refresh). */
	private confirmCloseIntent: 'drawer' | 'page-refresh' | null = null;

	/** Navigation API reload intercept — resolve to allow refresh, reject to cancel. */
	private reloadNavigationFinish: (() => void) | null = null;
	private reloadNavigationCancel: (() => void) | null = null;
	private pageReloadNavigateListener: ((event: Event) => void) | null = null;

	activeStudent: any;
	students: any[] = [];
	questions: any[] = [];
	private assessmentId: string | null = null;
	/** UI context that should survive roster swaps (badge colors, hints, etc.). */
	public lmsContext: ParaLmsDrawerContext | null = null;

	iframeUrl: SafeResourceUrl | null = null;
	/** Forces iframe reload when the LMS path matches the previous load (Angular may skip same URL). */
	private lastStoryUrlPath: string | null = null;
	confirmScore: number;
	isLoadingIframe = false;
	isSubmitting = false;
	isLocked = false;
	lockReason: string | null = null;
	private assessmentSeriesBlocked = false;
	private activeAssessmentSlug(): string | null {
		const raw = this.assessmentId ?? this.currentAssessmentId();
		const s = raw != null ? String(raw).trim() : '';
		return s ? s : null;
	}

	isScreenerLmsAssessment(): boolean {
		return isParaScreenerStyleAssessment(this.activeAssessmentSlug());
	}

	isTransformLmsAssessment(): boolean {
		return isParaTransformStyleAssessment(this.activeAssessmentSlug());
	}

	/** Transform: submit pending scores from the drawer header. */
	showLmsHeaderSubmit(): boolean {
		return !this.isLocked && this.isTransformLmsAssessment() && this.scoreStorageService.count > 0 && this.drawerState === 'assessing';
	}

	selectedDate: string;
	showDateSelector: boolean;
	drawerEntered = false;

	public scoreRubric = PARA_SCORE_RUBRIC;

	private mutationObserver: MutationObserver | null = null;
	private observedIframe: HTMLIFrameElement | null = null;
	private iframeLoadHandler: (() => void) | null = null;
	private storyHintRefreshTimer: ReturnType<typeof setTimeout> | undefined;
	private closeDrawerTimer: ReturnType<typeof setTimeout> | undefined;
	/** Node id used to load the roster when the drawer opened (progress table override). Submit/refresh must match this, not only {@link ParaOrgStateService.getSelectedClassroomSnapshot}. */
	private assessmentContextNodeId: number | null = null;

	/** Cached `getAssessmentContext` for LMS UI (e.g. Save & Done stop rules). */
	private lmsAssessmentContext: ParaAssessmentContextDto | null = null;

	/**
	 * Storyline slide key for scorer copy (`Para_Lms_Header_Supplement.lmsHeaderId`).
	 * Set from published story via `parent.paraLmsSetActiveHeader('slide2')` on slide enter.
	 */
	private activeLmsHeaderId: string | null = null;

	/** Visible Storyline score buttons (1–4), refreshed from the iframe DOM. */
	private storyVisibleScoreButtons: ParaScoreButton[] = [];
	/** Layout probes for visible score buttons (position + optional IO ratio). */
	private storyScoreButtonProbes: StoryScoreButtonProbe[] = [];
	private storyIframeViewWidth = 0;
	/** Whether the in-slide NEXT control is currently visible in the iframe. */
	private storyNextControlVisible = false;
	private scoreButtonIntersectionRatios = new Map<ParaScoreButton, number>();
	private scoreButtonIntersectionObserver: IntersectionObserver | null = null;

	/** User toggle for the active-slide scoring guide overlay. */
	lmsSlideGuideVisible = false;
	lmsSlideGuidePanelStyle: Record<string, string> = {};
	private lmsStableSlideGuideLines: string[] = [];
	private lmsSlideGuideResizeTimer: ReturnType<typeof setTimeout> | undefined;
	private lastLmsSlideGuideKey: string | null = null;

	private computeLmsActiveSlideGuideLines(): string[] {
		if (!this.drawerOpen || this.drawerState !== 'assessing') {
			return [];
		}
		return buildLmsActiveSlideGuideLines({
			supplements: this.lmsHeaderSupplements,
			activeLmsHeaderId: this.activeLmsHeaderId,
			scoreButtonProbes: this.storyScoreButtonProbes,
			viewWidth: this.storyIframeViewWidth,
			visibleScoreButtons: this.storyVisibleScoreButtons,
			nextControlVisible: this.storyNextControlVisible,
		});
	}

	private refreshLmsStableSlideGuideLines(): void {
		const lines = this.computeLmsActiveSlideGuideLines();
		this.lmsStableSlideGuideLines = lines;
	}

	/** Active slide scoring guide lines (from Word doc), shown when story controls are visible. */
	get lmsActiveSlideGuideLines(): string[] {
		if (!this.drawerOpen || this.drawerState !== 'assessing') {
			return [];
		}
		return this.lmsStableSlideGuideLines;
	}

	lmsSlideGuideLineSegments(line: string) {
		return parseScoringGuideLineSegments(line);
	}

	toggleLmsSlideGuide(): void {
		if (this.lmsSlideGuideVisible) {
			this.lmsSlideGuideVisible = false;
			this.lmsSlideGuidePanelStyle = {};
			return;
		}
		this.updateLmsSlideGuidePanelPosition();
		this.lmsSlideGuideVisible = true;
		this.cdr.detectChanges();
	}

	private updateLmsSlideGuidePanelPosition(): void {
		const btn = this.document.querySelector('[data-testid="para-lms-slide-guide-show"]') as HTMLElement | null;
		if (!btn) {
			return;
		}
		// Fixed descendants are positioned against the drawer, not the viewport (drawer uses transform).
		const drawer = btn.closest('.drawer') as HTMLElement | null;
		if (!drawer) {
			return;
		}
		const btnRect = btn.getBoundingClientRect();
		const drawerRect = drawer.getBoundingClientRect();
		const width = Math.min(352, drawerRect.width - 16);
		const btnCenterX = btnRect.left + btnRect.width / 2;
		let left = btnCenterX - drawerRect.left - width / 2;
		const minLeft = 8;
		const maxLeft = drawerRect.width - width - 8;
		left = Math.max(minLeft, Math.min(left, maxLeft));
		this.lmsSlideGuidePanelStyle = {
			top: `${btnRect.bottom - drawerRect.top + 6}px`,
			left: `${left}px`,
			width: `${width}px`,
		};
		this.cdr.markForCheck();
	}

	private syncLmsSlideGuideVisibilityForActiveSlide(): void {
		this.refreshLmsStableSlideGuideLines();
		const lines = this.computeLmsActiveSlideGuideLines();
		if (!lines.length) {
			this.lastLmsSlideGuideKey = null;
			this.lmsSlideGuideVisible = false;
			return;
		}
		const key = lines.join('\n');
		if (key !== this.lastLmsSlideGuideKey) {
			this.lastLmsSlideGuideKey = key;
			this.lmsSlideGuideVisible = false;
		}
	}

	constructor(
		private paraProgressReport: ParaProgressReportService,
		private paraOrgState: ParaOrgStateService,
		private paraRoster: ParaRosterService,
		private paraScoreSubmit: ParaScoreSubmitService,
		private sanitizer: DomSanitizer,
		private ngZone: NgZone,
		public scoreStorageService: ParaScoreStorageService,
		private paraLmsService: ParaLmsAssessmentService,
		private readonly paraSelectedAssessment: ParaSelectedAssessmentService,
		private readonly paraSeries: ParaAssessmentSeriesService,
		private readonly paraAssessmentContext: ParaAssessmentContextService,
		private cdr: ChangeDetectorRef,
		private vcr: ViewContainerRef,
		private appRef: ApplicationRef,
		private injector: Injector,
		private componentFactoryResolver: ComponentFactoryResolver,
		@Inject(DOCUMENT) private document: Document
	) {}

	private currentAssessmentId(): string | null {
		// Selected assessment drives the roster questions + LMS traversal.
		// Falls back to whatever the roster response returns.
		const raw = (this.paraSelectedAssessment as any)?.selectedAssessmentIdSubject?.value ?? null;
		const s = raw != null ? String(raw).trim() : '';
		return s ? s : null;
	}

	get drawerOpen(): boolean {
		return this.drawerState !== 'closed';
	}

	get showCloseConfirm(): boolean {
		return this.drawerState === 'confirmClose';
	}

	/** Body copy verb for the discard modal ("Closing" vs "Refreshing"). */
	get confirmDiscardActionLabel(): string {
		return this.confirmCloseIntent === 'page-refresh' ? 'Refreshing' : 'Closing';
	}

	/** Primary discard button label for the discard modal. */
	get confirmDiscardButtonLabel(): string {
		return this.confirmCloseIntent === 'page-refresh' ? 'Discard & Refresh' : 'Discard & Close';
	}

	/** Back-compat for tests / callers expecting `isOpen`. */
	get isOpen(): boolean {
		return this.drawerOpen;
	}

	ngOnInit(): void {
		const today = new Date();
		this.selectedDate = today.toISOString().split('T')[0];

		this.paraSelectedAssessment.activeSeriesItem$.pipe(takeUntil(this.destroy$)).subscribe((item) => {
			this.isLocked = item?.isLocked ?? false;
			this.lockReason = item?.lockReason ?? null;
			this.assessmentSeriesBlocked = !!(item?.isLocked || item?.isReadOnly);
			this.cdr.markForCheck();
		});

		this.paraLmsService.open$
			.pipe(
				filter((s) => s != null),
				takeUntil(this.destroy$)
			)
			.subscribe((payload: any) => {
				// New shape: { partial, context }. Back-compat: payload itself can be the partial.
				const partial = payload?.partial != null ? payload.partial : payload;
				const ctx: ParaLmsDrawerContext | null = payload?.context ?? null;
				const nodeId = ctx?.nodeId ?? null;
				this.lmsContext = ctx;
				debugParaLms('open$ received', { partial, nodeId, ctx, rawPayload: payload });
				this.resolveAndOpen(partial, nodeId);
			});

		this.setupPageReloadGuard();
	}

	ngAfterViewChecked(): void {
		this.syncScoreReviewPortal();
	}

	ngOnDestroy(): void {
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
		}
		this.detachScoreReviewPortal();
		this.teardownPageReloadGuard();
		this.destroy$.next();
		this.destroy$.complete();
		this.closeDrawerImmediate();
	}

	private syncScoreReviewPortal(): void {
		const shouldShow = this.drawerOpen && this.drawerState === 'confirmation';
		if (shouldShow === this.scoreReviewPortalVisible) {
			return;
		}
		this.scoreReviewPortalVisible = shouldShow;
		if (shouldShow) {
			this.attachScoreReviewPortal();
			this.document.body.classList.add('para-lms-score-review-open');
			return;
		}
		this.detachScoreReviewPortal();
		this.document.body.classList.remove('para-lms-score-review-open');
	}

	private attachScoreReviewPortal(): void {
		if (this.scoreReviewPortalRef || !this.scoreReviewPortal) {
			return;
		}
		const portal = new TemplatePortal(this.scoreReviewPortal, this.vcr);
		this.scoreReviewPortalOutlet = new DomPortalOutlet(
			this.document.body,
			this.componentFactoryResolver,
			this.appRef,
			this.injector
		);
		this.scoreReviewPortalRef = this.scoreReviewPortalOutlet.attach(portal) as EmbeddedViewRef<unknown>;
	}

	private detachScoreReviewPortal(): void {
		if (this.scoreReviewPortalOutlet?.hasAttached()) {
			this.scoreReviewPortalOutlet.detach();
		}
		this.scoreReviewPortalOutlet = null;
		this.scoreReviewPortalRef = null;
		this.document.body.classList.remove('para-lms-score-review-open');
	}

	@HostListener('document:keydown.escape')
	onEscKey(): void {
		if (this.drawerOpen) {
			this.closeDrawer();
		}
	}

	/**
	 * Tab close / navigate away when Navigation API cannot intercept (e.g. closing the tab).
	 * Toolbar refresh uses {@link setupPageReloadGuard} so the same custom modal can appear.
	 */
	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(event: BeforeUnloadEvent): void {
		if (!this.shouldGuardUnsavedScores() || this.reloadNavigationFinish != null) {
			return;
		}
		event.preventDefault();
		event.returnValue = '';
	}

	/** F5 / Ctrl+R / Cmd+R — show the same discard modal as closing the drawer. */
	@HostListener('document:keydown', ['$event'])
	onDocumentKeydown(event: KeyboardEvent): void {
		if (!this.shouldGuardUnsavedScores()) {
			return;
		}
		const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && (event.key === 'r' || event.key === 'R'));
		if (!isRefresh) {
			return;
		}
		event.preventDefault();
		this.promptUnsavedScoresDiscard('page-refresh');
	}

	private shouldGuardUnsavedScores(): boolean {
		return (
			this.drawerOpen &&
			this.scoreStorageService.count > 0 &&
			this.drawerState !== 'confirmClose' &&
			this.drawerState !== 'submitting' &&
			this.drawerState !== 'done'
		);
	}

	private promptUnsavedScoresDiscard(intent: 'drawer' | 'page-refresh'): void {
		if (!this.shouldGuardUnsavedScores()) {
			return;
		}
		this.confirmCloseIntent = intent;
		this.drawerState = 'confirmClose';
		this.cdr.markForCheck();
	}

	/**
	 * Intercepts browser-toolbar reload so we can show the same discard modal as closing the drawer.
	 * Falls back to keyboard intercept + `beforeunload` when `window.navigation` is unavailable.
	 */
	private setupPageReloadGuard(): void {
		const navigation = (window as Window & { navigation?: ParaWindowNavigation }).navigation;
		if (!navigation?.addEventListener) {
			return;
		}

		this.pageReloadNavigateListener = (raw: Event) => {
			const event = raw as ParaNavigationReloadEvent;
			if (!event.canIntercept || event.navigationType !== 'reload' || !this.shouldGuardUnsavedScores()) {
				return;
			}
			event.intercept?.({
				handler: () => this.waitForRefreshDiscardConfirmation(),
			});
		};
		navigation.addEventListener('navigate', this.pageReloadNavigateListener);
	}

	private teardownPageReloadGuard(): void {
		const navigation = (window as Window & { navigation?: ParaWindowNavigation }).navigation;
		if (navigation && this.pageReloadNavigateListener) {
			navigation.removeEventListener('navigate', this.pageReloadNavigateListener);
		}
		this.pageReloadNavigateListener = null;
		this.clearPendingReloadNavigation();
	}

	private clearPendingReloadNavigation(): void {
		this.reloadNavigationFinish = null;
		this.reloadNavigationCancel = null;
	}

	private waitForRefreshDiscardConfirmation(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.reloadNavigationFinish = resolve;
			this.reloadNavigationCancel = () => reject(new DOMException('Navigation aborted', 'AbortError'));
			this.ngZone.run(() => this.promptUnsavedScoresDiscard('page-refresh'));
		});
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (this.observedIframe) {
			this.scheduleStoryControlHintRefresh(this.observedIframe);
		}
		if (!this.lmsSlideGuideVisible) {
			return;
		}
		if (this.lmsSlideGuideResizeTimer != null) {
			clearTimeout(this.lmsSlideGuideResizeTimer);
		}
		this.lmsSlideGuideResizeTimer = setTimeout(() => {
			this.lmsSlideGuideResizeTimer = undefined;
			this.updateLmsSlideGuidePanelPosition();
		}, 100);
	}

	getCurrentChallenge(): any {
		return this.activeStudent?.challange?.nextChallenge;
	}

	/** Template helper: "challenged" = we have a current/next challenge object to score. */
	isChallenged(): boolean {
		const c = this.getCurrentChallenge();
		return c != null && typeof c === 'object';
	}

	/**
	 * 1-based challenge number used for LMS assets and UI.
	 *
	 * PARA LMS uses `questionNumber` (already 1-based folder number).
	 */
	getCurrentChallengeNumber(): number | null {
		const c = this.getCurrentChallenge();
		if (c == null || typeof c !== 'object') return null;

		// Preferred: already 1-based challenge folder number (used by LMS assets).
		const qn = Number((c as any).questionNumber);
		if (Number.isFinite(qn) && qn > 0) return Math.floor(qn);

		return null;
	}

	/**
	 * "Save & Done" visibility: requires `lms.saveDone` in assessment context `stopRules`
	 * and current challenge >= `minInclusiveChallenge` (default 2).
	 */
	showSaveAndDoneFromAssessmentContext(): boolean {
		if (!this.isChallenged()) return false;
		const rules = this.lmsAssessmentContext?.stopRules;
		if (!Array.isArray(rules) || rules.length === 0) return false;
		const rule = rules.find((r) => r && r.ruleId === 'lms.saveDone');
		if (!rule) return false;
		const raw = rule.params != null ? (rule.params as Record<string, unknown>)['minInclusiveChallenge'] : undefined;
		let minInclusive = 2;
		if (raw !== undefined && raw !== null) {
			const n = Number(raw);
			if (Number.isFinite(n) && n > 0) minInclusive = Math.floor(n);
		}
		const cur = this.getCurrentChallengeNumber();
		if (cur == null) return false;
		return cur >= minInclusive;
	}

	/**
	 * Hide "Save & Next" when the catalog has no following question in this student's phase
	 * (same resolution as {@link storeScore}).
	 */
	hasNextCatalogChallengeAfterCurrent(): boolean {
		if (!this.isChallenged() || !this.activeStudent) {
			return false;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return (
			getNextChallengeQuestionAfter(q, this.questions, this.activeStudent.assessmentBookId, this.activeStudent.assessmentTemplateId) != null
		);
	}

	hasPreviousCatalogChallengeBeforeCurrent(): boolean {
		if (!this.isChallenged() || !this.activeStudent) {
			return false;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return (
			getPreviousChallengeQuestionBefore(
				q,
				this.questions,
				this.activeStudent.assessmentBookId,
				this.activeStudent.assessmentTemplateId
			) != null
		);
	}

	canGoToPreviousChallenge(): boolean {
		return this.drawerState === 'assessing' && !this.isLocked && this.hasPreviousCatalogChallengeBeforeCurrent();
	}

	/** True when Storyline reports slide 1 (or before the first slide enter callback). */
	isOnFirstSlideOfChallenge(): boolean {
		return isFirstSlideOfLmsChallenge({
			activeLmsHeaderId: this.activeLmsHeaderId,
			scoreButtonProbes: this.storyScoreButtonProbes,
			viewWidth: this.storyIframeViewWidth,
			supplements: this.lmsHeaderSupplements,
			visibleScoreButtons: this.storyVisibleScoreButtons,
		});
	}

	goToPreviousChallenge(): void {
		this.logPrevCrossChallengeDebug('goToPreviousChallenge called');
		if (!this.canGoToPreviousChallenge() || !this.activeStudent?.challange) {
			this.logPrevCrossChallengeDebug('goToPreviousChallenge aborted — not allowed or no active challenge', {
				hasActiveChallenge: !!this.activeStudent?.challange,
			});
			return;
		}
		this.resumeStorageOverride.prepareForFreshChallengeLoad();
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const prevQuestion = getPreviousChallengeQuestionBefore(
			q,
			this.questions,
			this.activeStudent.assessmentBookId,
			this.activeStudent.assessmentTemplateId
		);
		if (!prevQuestion) {
			this.logPrevCrossChallengeDebug('goToPreviousChallenge aborted — no previous question in catalog');
			return;
		}
		this.logPrevCrossChallengeDebug('goToPreviousChallenge loading previous question', {
			prevQuestionId: prevQuestion.id,
			prevQuestionNumber: prevQuestion.questionNumber,
			prevLabel: prevQuestion.label,
		});
		this.activeStudent.challange.nextChallenge = prevQuestion;
		this.drawerState = 'assessing';
		this.loadStoryIframe(this.activeStudent);
		this.cdr.markForCheck();
	}

	navigateToChallengeForRescore(storageKey: string): void {
		if (this.isLocked) {
			return;
		}
		const stored = this.scoreStorageService.storedScores[String(storageKey)];
		const question = stored?.question;
		if (!question || !this.activeStudent?.challange) {
			return;
		}
		this.activeStudent.challange.nextChallenge = normalizeNextChallengeForLms(question, this.questions) ?? question;
		this.drawerState = 'assessing';
		this.loadStoryIframe(this.activeStudent);
		this.cdr.markForCheck();
	}

	getCurrentChallengeDisplay(): string {
		return formatChallengeDisplay(this.getCurrentChallengeNumber());
	}

	getCurrentChallengeLabelClass(): string {
		const q = this.getCurrentChallenge();
		const testId = q?.testId;
		return testId != null ? `labelcolor-${testId}` : 'tw-bg-slate-100 tw-text-para-primary tw-border tw-border-slate-200';
	}

	getHighestChallengePhaseLabel(): string | null {
		// API shapes vary: some responses nest under `challange.highestChallenge`,
		// others may include `highestChallenge` at the root.
		return (
			(this.lmsContext?.highestChallenge as any)?.phase?.shortLabel ??
			this.activeStudent?.highestChallenge?.phase?.shortLabel ??
			this.activeStudent?.challange?.highestChallenge?.phase?.shortLabel ??
			null
		);
	}

	getHighestChallengePhaseColor(): string | null {
		const raw =
			(this.lmsContext?.highestChallenge as any)?.phase?.color ??
			this.activeStudent?.highestChallenge?.phase?.color ??
			this.activeStudent?.challange?.highestChallenge?.phase?.color ??
			null;
		if (raw == null || String(raw).trim() === '') return null;
		const s = String(raw).trim();
		return s.startsWith('#') ? s : `#${s}`;
	}

	getHighestChallengeDisplay(): string {
		const n = Number(
			(this.lmsContext?.highestChallenge as any)?.challengeId ??
				this.activeStudent?.highestChallenge?.challengeId ??
				this.activeStudent?.challange?.highestChallenge?.challengeId
		);
		if (!Number.isFinite(n) || n < 0) return '0';
		return String(Math.floor(n)).padStart(2, '0');
	}

	/** Milestones for drawer header (from progress table context; mirrors student detail). */
	get lmsMilestonesWithStatus(): ParaMilestoneWithStatus[] {
		const list = this.lmsContext?.milestonesWithStatus;
		return Array.isArray(list) ? list : [];
	}

	get lmsStudentBadgeMilestoneOverlay(): ParaStudentBadgeMilestoneOverlay | null {
		return resolveStudentBadgeMilestoneOverlay(this.lmsMilestonesWithStatus);
	}

	/** Supplementary scorer copy for the challenge currently loaded in the LMS story. */
	get lmsHeaderSupplements(): ParaLmsHeaderSupplementDto[] {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		return resolveLmsHeaderSupplementsForChallenge(q, this.questions, this.lmsAssessmentContext?.lmsHeaderSupplements);
	}

	get lmsActiveSlideSupplements(): ParaLmsHeaderSupplementDto[] {
		return filterLmsHeaderSupplementsForActiveSlide(this.lmsHeaderSupplements, this.activeLmsHeaderId);
	}

	/** Challenge title for the drawer header (prefer named label over bare challenge number). */
	get lmsCurrentChallengeLabel(): string | null {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		if (!q || typeof q !== 'object') {
			return null;
		}
		const label = String((q as { label?: string }).label ?? '').trim();
		const question = String((q as { question?: string }).question ?? '').trim();
		const num = this.getCurrentChallengeNumber();
		const numStr = num != null ? String(num) : '';
		if (label && label !== numStr) {
			return label;
		}
		return question || null;
	}

	/** Current challenge index within the phase catalog (e.g. `2 / 15`). */
	get lmsChallengeNavLabel(): string | null {
		if (!this.isChallenged() || !this.activeStudent) {
			return null;
		}
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const pos = getChallengePositionInCatalog(q, this.questions, this.activeStudent);
		if (!pos) {
			const n = this.getCurrentChallengeNumber();
			const total = getPhaseQuestionCatalog(
				this.questions,
				this.activeStudent.assessmentBookId,
				this.activeStudent.assessmentTemplateId,
				q
			).length;
			if (n != null && total > 0) {
				return `${n} / ${total}`;
			}
			return null;
		}
		return `${pos.position} / ${pos.total}`;
	}

	get lmsDrawerStudentName(): string {
		const name = this.lmsContext?.drawerHeader?.name?.trim();
		if (name) return name;
		const f = (this.activeStudent?.student?.firstName ?? '').toString().trim();
		const l = (this.activeStudent?.student?.lastName ?? '').toString().trim();
		return `${f} ${l}`.trim() || 'Student';
	}

	get lmsDrawerStudentGrade(): string {
		const g = this.lmsContext?.drawerHeader?.grade;
		if (g != null && String(g).trim() !== '') return String(g).trim();
		const alt = this.activeStudent?.grade ?? this.activeStudent?.student?.grade;
		return alt != null ? String(alt).trim() : '';
	}

	get lmsDrawerBadgePhase(): string | number | null {
		const h = this.lmsContext?.drawerHeader;
		if (h) return h.badgePhase ?? null;
		return this.getHighestChallengePhaseLabel();
	}

	get lmsDrawerBadgeChallenge(): string | number {
		const h = this.lmsContext?.drawerHeader;
		if (h) return h.badgeChallenge ?? '—';
		return this.getHighestChallengeDisplay();
	}

	get lmsDrawerBadgeBg(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.bg ??
			this.lmsContext?.badgeColors?.bg ??
			(this.activeStudent as any)?.__paraBadge?.bg ??
			null
		);
	}

	get lmsDrawerBadgeFg(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.fg ??
			this.lmsContext?.badgeColors?.fg ??
			(this.activeStudent as any)?.__paraBadge?.fg ??
			null
		);
	}

	get lmsDrawerBadgeBorder(): string | null {
		return (
			this.lmsContext?.drawerHeader?.badgeColors?.border ??
			this.lmsContext?.badgeColors?.border ??
			(this.activeStudent as any)?.__paraBadge?.border ??
			null
		);
	}

	/** Same precedence as student detail row phase color, with LMS hex normalization as fallback. */
	getLmsPhaseChipColor(): string | null {
		const explicit = this.lmsContext?.phaseChipColor;
		const normalized = normalizeChipColor(explicit);
		if (normalized) return normalized;
		return this.getHighestChallengePhaseColor();
	}

	private loadLmsAssessmentContext(): void {
		const nid = this.assessmentContextNodeId;
		if (nid == null) {
			return;
		}
		this.lmsAssessmentContext = null;
		this.paraAssessmentContext
			.getAssessmentContext(nid, this.assessmentId)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				catchError(() => of(null))
			)
			.subscribe((ctx) => {
				this.lmsAssessmentContext = ctx;
				this.cdr.markForCheck();
			});
	}

	private resolveAndOpen(partial: any, nodeIdOverride?: unknown): void {
		if (this.drawerOpen) {
			this.closeDrawerImmediate();
		}
		const classroomId = normalizeNodeId(nodeIdOverride) ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
		debugParaLms('resolveAndOpen', {
			nodeIdOverride,
			paraSelectedClassroomId: this.paraOrgState.getSelectedClassroomSnapshot()?.id,
			classroomId,
			partialStudentId: partial?.id,
		});
		if (classroomId == null) {
			debugParaLms('abort: no classroomId');
			console.warn('[PARA LMS] No classroom selected; drawer will not open. Wait for dashboard load or select a classroom.');
			return;
		}
		if (partial?.id == null) {
			debugParaLms('abort: no partial.id');
			console.warn('[PARA LMS] Could not identify the student (missing id); drawer will not open.');
			return;
		}
		this.paraRoster
			.getStudents$(classroomId, this.currentAssessmentId())
			.pipe(take(1))
			.subscribe({
				next: (data: any) => {
					this.assessmentId = data?.assessment?.assessmentId ? String(data.assessment.assessmentId) : null;
					const rosterStudents = Array.isArray(data?.students) ? data.students : [];
					const fromRoster = Array.isArray(data?.questions) ? data.questions : [];
					const questions = fromRoster;
					this.assessmentContextNodeId = classroomId;
					this.questions = questions;
					const assessableStudents = this.filterAssessableRosterStudents(rosterStudents);
					this.students = assessableStudents;
					const full = assessableStudents.find((s: any) => String(s.id) === String(partial.id));
					debugParaLms('getStudents$ roster', {
						rosterStudentCount: rosterStudents.length,
						assessableStudentCount: assessableStudents.length,
						rosterQuestionCount: fromRoster.length,
						foundStudent: !!full,
						rawChallangeBeforeEffective: full ? { ...full.challange } : null,
					});
					if (!full) {
						const clicked = rosterStudents.find((s: any) => String(s.id) === String(partial.id));
						this.prepareStudentForLms(clicked ?? partial, partial);
						const blockReason = getParaLmsStartBlockReason(clicked ?? partial, {
							seriesBlocked: this.assessmentSeriesBlocked,
							catalogChallengeCount: questions.length,
						});
						debugParaLms('abort: student not assessable in LMS roster', {
							lookedForId: partial.id,
							blockReason,
							assessableCount: assessableStudents.length,
						});
						console.warn(
							'[PARA LMS] Student cannot be assessed in the LMS drawer (exempt, finished, or locked).',
							blockReason ?? { id: partial.id }
						);
						return;
					}
					this.loadLmsAssessmentContext();

					this.prepareStudentForLms(full, partial);
					debugParaLms('resolved story path (before drawer)', {
						storyPath: lmsStoryUrlFromNextChallenge(full?.challange?.nextChallenge, this.assessmentId),
						nextChallengeUsed: full?.challange?.nextChallenge,
						assessableRosterSize: assessableStudents.length,
					});
					this.openDrawerWithStudent(full);
				},
				error: (err: unknown) => {
					debugParaLms('getStudents$ error', err);
					console.error('[PARA LMS] Could not load students for this class.', err);
				},
			});
	}

	/**
	 * Roster slice for the LMS drawer: non-exempt students with a remaining challenge (not finished).
	 */
	private filterAssessableRosterStudents(rosterStudents: any[]): any[] {
		const opts = {
			seriesBlocked: this.assessmentSeriesBlocked,
			catalogChallengeCount: Array.isArray(this.questions) ? this.questions.length : 0,
		};
		const out: any[] = [];
		for (const raw of rosterStudents) {
			if (!raw) {
				continue;
			}
			const row = {
				...raw,
				student: raw.student ? { ...raw.student } : raw.student,
				challange: raw.challange ? { ...raw.challange } : raw.challange,
			};
			this.prepareStudentForLms(row, null);
			if (canStartParaLmsAssessment(row, opts)) {
				out.push(row);
			}
		}
		return out;
	}

	/** Merge progress-row hints and resolve nextChallenge for LMS iframe + scoring. */
	private prepareStudentForLms(full: any, partial: any | null): void {
		if (!full) {
			return;
		}
		mergeProgressRowChallangeHints(full, partial);
		const badgeFromHeader = this.lmsContext?.drawerHeader?.badgeColors ?? null;
		const badgeFromContext = this.lmsContext?.badgeColors ?? badgeFromHeader ?? null;
		const badgeFromPartial = partial?.__paraBadge ?? null;
		const badge = badgeFromContext ?? badgeFromPartial;
		if (badge != null) {
			(full as any).__paraBadge = badge;
		}
		full.challange = full.challange || {};
		const hasApiLmsChallenges = Object.prototype.hasOwnProperty.call(full, 'nextChallenge');
		if (hasApiLmsChallenges) {
			full.challange.nextChallenge = full.nextChallenge ?? undefined;
		} else {
			const effective = getEffectiveNextChallenge(full, this.questions);
			if (effective) {
				full.challange.nextChallenge = effective;
			}
		}
		const nc = full.challange?.nextChallenge;
		if (nc && typeof nc === 'object') {
			full.challange.nextChallenge = normalizeNextChallengeForLms(nc, this.questions);
		}
	}

	private openDrawerWithStudent(student: any): void {
		this.disconnectStoryObservers();
		this.activeLmsHeaderId = null;
		this.storyVisibleScoreButtons = [];
		this.storyNextControlVisible = false;
		this.activeStudent = student;

		this.drawerState = 'assessing';
		this.drawerEntered = false;
		this.loadStoryIframe(student);
		this.cdr.detectChanges();
		// Use a macrotask to ensure the drawer DOM is in place before we toggle the open class.
		// This is more reliable than nested rAF when other overlays just unmounted.
		setTimeout(() => {
			this.drawerEntered = true;
			this.cdr.markForCheck();
		}, 0);
	}

	private loadStoryIframe(student: any): void {
		this.resumeStorageOverride.prepareForFreshChallengeLoad();
		this.disconnectStoryObservers();
		this.activeLmsHeaderId = null;
		this.storyVisibleScoreButtons = [];
		this.storyNextControlVisible = false;
		this.clearWindowStoryHooks();
		const nc = student?.challange?.nextChallenge;
		const urlPath = lmsStoryUrlFromNextChallenge(nc, this.assessmentId);
		debugParaLms('loadStoryIframe', {
			urlPath,
			nextChallenge: nc,
			sourceFileHint: urlPath != null ? String(urlPath).replace(/^\//, 'src/') : null,
		});
		if (!urlPath) {
			this.lastStoryUrlPath = null;
			this.iframeUrl = null;
			console.warn("[PARA LMS] No LMS story available for this student's current challenge (missing or completed).");
			this.cdr.detectChanges();
			return;
		}
		const assignSrc = () => {
			this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlPath);
			this.lastStoryUrlPath = urlPath;
			this.setWindowStoryHooks();
			this.cdr.detectChanges();
		};
		if (urlPath === this.lastStoryUrlPath) {
			this.iframeUrl = null;
			this.cdr.detectChanges();
			requestAnimationFrame(() => assignSrc());
			return;
		}
		this.isLoadingIframe = true;
		assignSrc();
	}

	private isStorylineResumeLikeValue(value: string): boolean {
		if (!value || typeof value !== 'string') {
			return false;
		}
		return (
			value.includes('^v_player') ||
			value.includes('~_player') ||
			value.includes('"slideId"') ||
			value.includes('"suspendData"') ||
			/PlayerMemory/i.test(value)
		);
	}

	onStoryIframeLoaded(): void {
		const iframe = document.querySelector('#para-lms-assessment-frame') as HTMLIFrameElement;

		if (iframe && iframe.contentDocument) {
			try {
				const style = iframe.contentDocument.createElement('style');
				style.textContent = `
					body, html, #bg, .story-bg, #playback, #frame {
						background-color: #243670 !important;
					}
					${LmsAssessmentSlideoutComponent.STORY_HIDDEN_ACC_TEXTS.map(
						(accText) => `[data-acc-text="${accText}"] {
						display: none !important;
						visibility: hidden !important;
						pointer-events: none !important;
					}`
					).join('\n')}
				`;
				iframe.contentDocument.head.appendChild(style);
				this.hideStoryHiddenAccTextElements(iframe.contentDocument);
				this.dismissStoryResumePrompt(iframe.contentDocument);
			} catch (e) {
				console.warn('Could not inject styles into iframe', e);
			}
		}

		this.isLoadingIframe = false;
		if (iframe) {
			this.setupMutationObserver(iframe);
			this.scheduleStoryControlHintRefresh(iframe);
		}
	}

	closeDrawer(): void {
		if (!this.drawerOpen) {
			return;
		}
		if (this.drawerState === 'scoring') {
		}
		if (this.scoreStorageService.count > 0) {
			this.promptUnsavedScoresDiscard('drawer');
			return;
		}
		this.drawerEntered = false;
		this.cdr.markForCheck();
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
		}
		this.closeDrawerTimer = setTimeout(() => {
			this.closeDrawerImmediate();
			this.closeDrawerTimer = undefined;
		}, 320);
	}

	confirmCloseDrawer(): void {
		const intent = this.confirmCloseIntent;
		this.confirmCloseIntent = null;
		this.scoreStorageService.clearData();
		if (intent === 'page-refresh') {
			const finishReload = this.reloadNavigationFinish;
			this.clearPendingReloadNavigation();
			if (finishReload) {
				finishReload();
				return;
			}
			window.location.reload();
			return;
		}
		this.clearPendingReloadNavigation();
		this.closeDrawerImmediate();
	}

	cancelCloseDrawer(): void {
		const cancelReload = this.reloadNavigationCancel;
		this.confirmCloseIntent = null;
		this.clearPendingReloadNavigation();
		if (cancelReload) {
			cancelReload();
		}
		this.drawerState = 'assessing';
		this.cdr.markForCheck();
	}

	private closeDrawerImmediate(): void {
		if (this.closeDrawerTimer != null) {
			clearTimeout(this.closeDrawerTimer);
			this.closeDrawerTimer = undefined;
		}
		this.assessmentContextNodeId = null;
		this.lmsAssessmentContext = null;
		this.activeLmsHeaderId = null;
		this.lmsSlideGuideVisible = false;
		this.lastLmsSlideGuideKey = null;
		this.lmsStableSlideGuideLines = [];
		if (this.lmsSlideGuideResizeTimer != null) {
			clearTimeout(this.lmsSlideGuideResizeTimer);
			this.lmsSlideGuideResizeTimer = undefined;
		}

		this.teardownStoryObservers();
		this.activeLmsHeaderId = null;
		this.clearWindowStoryHooks();
		this.resumeStorageOverride.disable();
		this.confirmCloseIntent = null;
		this.clearPendingReloadNavigation();
		this.drawerState = 'closed';
		this.drawerEntered = false;
		this.lastStoryUrlPath = null;
		this.iframeUrl = null;
		this.activeStudent = null;
		this.cdr.markForCheck();
	}

	private setWindowStoryHooks(): void {
		(window as any).scoreFromStoryLine = this.scoreFromStoryLine.bind(this);
		(window as any).paraLmsSetActiveHeader = this.setActiveLmsHeaderFromStoryLine.bind(this);
	}

	private clearWindowStoryHooks(): void {
		if ((window as any).scoreFromStoryLine) {
			delete (window as any).scoreFromStoryLine;
		}
		if ((window as any).paraLmsSetActiveHeader) {
			delete (window as any).paraLmsSetActiveHeader;
		}
	}

	/**
	 * Called from Storyline when a scored slide is shown.
	 * Example trigger JS: `parent.paraLmsSetActiveHeader('slide2');`
	 */
	public setActiveLmsHeaderFromStoryLine(headerId: string): void {
		const normalized = normalizeLmsHeaderId(headerId);
		if (!normalized) {
			return;
		}
		this.ngZone.run(() => {
			this.activeLmsHeaderId = normalized;
			this.lmsStableSlideGuideLines = [];
			debugParaLms('paraLmsSetActiveHeader', { raw: headerId, normalized });
			const iframe = document.getElementById('para-lms-assessment-frame') as HTMLIFrameElement | null;
			if (iframe) {
				this.scheduleStoryControlHintRefresh(iframe);
			}
			this.syncLmsSlideGuideVisibilityForActiveSlide();
			this.cdr.markForCheck();
		});
	}

	private disconnectStoryObservers(): void {
		if (this.storyHintRefreshTimer != null) {
			clearTimeout(this.storyHintRefreshTimer);
			this.storyHintRefreshTimer = undefined;
		}
		if (this.scoreButtonIntersectionObserver) {
			this.scoreButtonIntersectionObserver.disconnect();
			this.scoreButtonIntersectionObserver = null;
		}
		this.scoreButtonIntersectionRatios.clear();
		if (this.observedIframe && this.iframeLoadHandler) {
			this.observedIframe.removeEventListener('load', this.iframeLoadHandler);
		}
		this.observedIframe = null;
		this.iframeLoadHandler = null;
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}
	}

	private teardownStoryObservers(): void {
		this.disconnectStoryObservers();
		this.storyVisibleScoreButtons = [];
		this.storyScoreButtonProbes = [];
		this.storyIframeViewWidth = 0;
		this.storyNextControlVisible = false;
	}

	private setupMutationObserver(iframe: HTMLIFrameElement): void {
		this.disconnectStoryObservers();
		this.observedIframe = iframe;

		const config = { childList: true, subtree: true };
		const mutationCallback = (mutationsList: MutationRecord[]) => {
			const hasSignificantChanges = mutationsList.some(
				(m) => m.type === 'childList' && (m.addedNodes.length > 2 || m.removedNodes.length > 2)
			);
			if (hasSignificantChanges && this.drawerState === 'scoring') {
				this.ngZone.run(() => this.dismissScoreBar());
			}
			if (iframe.contentDocument) {
				this.hideStoryHiddenAccTextElements(iframe.contentDocument);
				this.dismissStoryResumePrompt(iframe.contentDocument);
			}
			this.ensureScoreButtonIntersectionObserver(iframe);
			this.ensureStoryPlayerPrevBridge(iframe);
			this.scheduleStoryControlHintRefresh(iframe);
			mutationsList.forEach((mutation) => {
				if (mutation.type === 'childList') {
					const elements = iframe.contentDocument?.querySelectorAll(
						'[data-model-id][data-acc-text="1"], [data-model-id][data-acc-text="2"], [data-model-id][data-acc-text="3"], [data-model-id][data-acc-text="4"]'
					);

					elements?.forEach((element) => {
						if (!(element as any).dataset.observed) {
							(element as any).dataset.observed = 'true';
							const intersectionObserver = new IntersectionObserver((entries) => {
								entries.forEach((entry) => {
									if (entry.isIntersecting) {
										const clone = entry.target.cloneNode(true) as HTMLElement;

										clone.style.position = 'absolute';
										clone.style.top = `${(entry.target as HTMLElement).offsetTop}px`;
										clone.style.left = `${(entry.target as HTMLElement).offsetLeft}px`;
										clone.style.zIndex = '1000';

										entry.target.parentElement?.appendChild(clone);

										clone.addEventListener('click', () => {
											const score = parseInt(clone.getAttribute('data-acc-text') || '0', 10);
											(window as any).scoreFromStoryLine(score);
										});

										intersectionObserver.disconnect();
									}
								});
							});

							intersectionObserver.observe(element);
						}
					});
				}
			});
		};

		this.mutationObserver = new MutationObserver(mutationCallback);

		const attachToBody = () => {
			this.mutationObserver?.disconnect();
			if (iframe.contentDocument?.body && this.mutationObserver) {
				this.mutationObserver.observe(iframe.contentDocument.body, config);
			}
			this.ensureScoreButtonIntersectionObserver(iframe);
			this.ensureStoryPlayerPrevBridge(iframe);
			this.scheduleStoryControlHintRefresh(iframe);
		};

		this.iframeLoadHandler = () => {
			attachToBody();
		};
		iframe.addEventListener('load', this.iframeLoadHandler);
		attachToBody();
	}

	/** Tracks intersection ratios for score buttons to support slide inference + hint refresh. */
	private ensureScoreButtonIntersectionObserver(iframe: HTMLIFrameElement): void {
		const doc = iframe.contentDocument;
		const win = doc?.defaultView;
		if (!doc || !win) {
			return;
		}

		if (!this.scoreButtonIntersectionObserver) {
			this.scoreButtonIntersectionObserver = new IntersectionObserver(
				(entries) => {
					let changed = false;
					for (const entry of entries) {
						const score = Number((entry.target as HTMLElement).getAttribute('data-acc-text'));
						if (!Number.isFinite(score) || score < 1 || score > 4) {
							continue;
						}
						this.scoreButtonIntersectionRatios.set(score as ParaScoreButton, entry.intersectionRatio);
						changed = true;
					}
					if (changed && this.observedIframe) {
						this.scheduleStoryControlHintRefresh(this.observedIframe);
					}
				},
				{ root: null, threshold: [0, 0.25, 0.5, 0.75, 1] }
			);
		}

		for (const score of [1, 2, 3, 4] as ParaScoreButton[]) {
			const el = this.findStoryScoreButtonElement(doc, score);
			if (!el || (el as HTMLElement).dataset.paraHintObserved === 'true') {
				continue;
			}
			(el as HTMLElement).dataset.paraHintObserved = 'true';
			this.scoreButtonIntersectionObserver.observe(el);
		}
	}

	private scheduleStoryControlHintRefresh(iframe: HTMLIFrameElement): void {
		if (this.storyHintRefreshTimer != null) {
			clearTimeout(this.storyHintRefreshTimer);
		}
		this.storyHintRefreshTimer = setTimeout(() => {
			this.storyHintRefreshTimer = undefined;
			this.refreshStoryControlHints(iframe);
		}, 80);
	}

	private refreshStoryControlHints(iframe: HTMLIFrameElement): void {
		if (!this.drawerOpen || this.drawerState !== 'assessing' || this.isLoadingIframe) {
			this.ngZone.run(() => {
				this.storyVisibleScoreButtons = [];
				this.storyScoreButtonProbes = [];
				this.storyIframeViewWidth = 0;
				this.storyNextControlVisible = false;
				this.cdr.markForCheck();
			});
			return;
		}

		const doc = iframe.contentDocument;
		const viewWidth = doc?.documentElement.clientWidth ?? 0;
		const probes = doc ? this.probeVisibleStoryScoreButtons(doc) : [];
		const visibleScoreButtons = probes.map((p) => p.button);
		const nextControlVisible = doc ? this.findVisibleStoryNextControl(doc) != null : false;
		if (doc) {
			this.primeStoryPlayerPrevCrossChallengeClick(doc);
		}

		this.ngZone.run(() => {
			this.storyScoreButtonProbes = probes;
			this.storyIframeViewWidth = viewWidth;
			this.storyVisibleScoreButtons = visibleScoreButtons;
			this.storyNextControlVisible = nextControlVisible;
			this.syncLmsSlideGuideVisibilityForActiveSlide();
			if (this.lmsSlideGuideVisible) {
				this.updateLmsSlideGuidePanelPosition();
			}
			this.cdr.markForCheck();
		});
	}

	private probeVisibleStoryScoreButtons(doc: Document): StoryScoreButtonProbe[] {
		const probes: StoryScoreButtonProbe[] = [];
		for (const score of [1, 2, 3, 4] as ParaScoreButton[]) {
			const el = this.findStoryScoreButtonElement(doc, score);
			if (!el) {
				continue;
			}
			const rect = el.getBoundingClientRect();
			probes.push({
				button: score,
				left: rect.left,
				top: rect.top,
				width: rect.width,
				height: rect.height,
				intersectionRatio: this.scoreButtonIntersectionRatios.get(score),
			});
		}
		return probes;
	}

	private findVisibleStoryScoreButtonNumbers(doc: Document): ParaScoreButton[] {
		return ([1, 2, 3, 4] as ParaScoreButton[]).filter((score) => this.findStoryScoreButtonElement(doc, score) != null);
	}

	private findStoryScoreButtonElement(doc: Document, score: ParaScoreButton): Element | null {
		const selectors = [`[data-model-id][data-acc-text="${score}"]`, `[data-acc-text="${score}"]`];
		let best: Element | null = null;
		let bestArea = 0;
		for (const selector of selectors) {
			for (const el of Array.from(doc.querySelectorAll(selector))) {
				if (!this.isStoryScoreButton(el)) {
					continue;
				}
				const rect = el.getBoundingClientRect();
				const area = rect.width * rect.height;
				if (area > bestArea) {
					bestArea = area;
					best = el;
				}
			}
		}
		return best;
	}

	private findVisibleStoryNextControl(doc: Document): HTMLElement | null {
		const selectors = [
			'[data-acc-text="NEXT"]',
			'[data-acc-text="Next"]',
			'[data-acc-text="next"]',
			'[aria-label*="NEXT" i]',
			'[aria-label*="Next" i]',
		];
		for (const selector of selectors) {
			const match = Array.from(doc.querySelectorAll(selector)).find(
				(el) => this.isStoryControlVisible(el) && !this.isStoryPlayerChrome(el)
			);
			if (match) {
				return match as HTMLElement;
			}
		}
		const fallback = Array.from(doc.querySelectorAll('[data-acc-text], [role="button"], button, [tabindex="0"]')).find((el) => {
			if (!this.isStoryControlVisible(el) || this.isStoryPlayerChrome(el)) {
				return false;
			}
			const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.trim();
			return /\bNEXT\b/i.test(label);
		});
		return (fallback as HTMLElement | undefined) ?? null;
	}

	/** Storyline player-bar PREV (cross-challenge back when on slide 1). */
	private isStoryPlayerPrevInChrome(el: Element): boolean {
		if (!this.isStoryPlayerChrome(el)) {
			return false;
		}
		const node = el as HTMLElement;
		const rect = node.getBoundingClientRect();
		if (rect.width <= 0 || rect.height <= 0) {
			return false;
		}
		const style = node.ownerDocument?.defaultView?.getComputedStyle(node);
		return !!style && style.display !== 'none' && style.visibility !== 'hidden';
	}

	private findStoryPlayerPrevControl(doc: Document): HTMLElement | null {
		const selectors = [
			'[data-acc-text="PREV"]',
			'[data-acc-text="Prev"]',
			'[data-acc-text="prev"]',
			'[aria-label*="PREV" i]',
			'[aria-label*="Previous" i]',
		];
		for (const selector of selectors) {
			const match = Array.from(doc.querySelectorAll(selector)).find((el) => this.isStoryPlayerPrevInChrome(el));
			if (match) {
				return match as HTMLElement;
			}
		}
		return (
			(Array.from(doc.querySelectorAll('[data-acc-text], [role="button"], button, [tabindex="0"]')).find((el) => {
				if (!this.isStoryPlayerPrevInChrome(el)) {
					return false;
				}
				const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.trim();
				return /\bPREV\b/i.test(label) || /\bPrevious\b/i.test(label);
			}) as HTMLElement | undefined) ?? null
		);
	}

	private resolveStoryPlayerPrevBridgeTarget(prevEl: HTMLElement): HTMLElement {
		return (
			(prevEl.closest('[role="button"], button, [class*="prev" i], .cs-button') as HTMLElement | null) ?? prevEl
		);
	}

	private logPrevCrossChallengeDebug(context: string, extra?: Record<string, unknown>): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		debugParaLms(`PREV cross-challenge: ${context}`, {
			drawerState: this.drawerState,
			isLocked: this.isLocked,
			canGoToPreviousChallenge: this.canGoToPreviousChallenge(),
			hasPreviousCatalogChallenge: this.hasPreviousCatalogChallengeBeforeCurrent(),
			isOnFirstSlideOfChallenge: this.isOnFirstSlideOfChallenge(),
			activeLmsHeaderId: this.activeLmsHeaderId,
			currentChallengeId: q?.id,
			currentQuestionNumber: q?.questionNumber,
			...extra,
		});
	}

	/** Re-enable clicks on disabled player PREV when we will jump to the prior catalog challenge. */
	private primeStoryPlayerPrevCrossChallengeClick(doc: Document): void {
		const prevEl = this.findStoryPlayerPrevControl(doc);
		if (!prevEl) {
			return;
		}
		const prevEnabled = this.isStoryPlayerPrevEnabledForNavigation(prevEl);
		if (prevEnabled || !this.canGoToPreviousChallenge()) {
			return;
		}
		const target = this.resolveStoryPlayerPrevBridgeTarget(prevEl);
		target.style.pointerEvents = 'auto';
		target.style.cursor = 'pointer';
		this.logPrevCrossChallengeDebug('primed disabled player PREV for cross-challenge click', {
			prevEnabled,
			bridgeTarget: target.tagName,
		});
	}

	private isStoryPlayerPrevEnabledForNavigation(prevEl: HTMLElement): boolean {
		const btn = (prevEl.closest('[role="button"], button, [tabindex]') as HTMLElement | null) ?? prevEl;
		if (btn.matches(':disabled, [aria-disabled="true"]')) {
			return false;
		}
		if (btn.getAttribute('tabindex') === '-1') {
			return false;
		}
		const style = btn.ownerDocument?.defaultView?.getComputedStyle(btn);
		return !!style && style.pointerEvents !== 'none';
	}

	/** Cross-challenge PREV on the plate's first slide (player PREV may still look enabled). */
	private shouldInterceptPrevForPreviousChallenge(_prevEl: HTMLElement): boolean {
		return this.canGoToPreviousChallenge() && this.isOnFirstSlideOfChallenge();
	}

	private ensureStoryPlayerPrevBridge(iframe: HTMLIFrameElement): void {
		const doc = iframe.contentDocument;
		if (!doc || this.drawerState !== 'assessing' || this.isLocked) {
			return;
		}
		this.primeStoryPlayerPrevCrossChallengeClick(doc);
		const prevEl = this.findStoryPlayerPrevControl(doc);
		if (!prevEl) {
			const prevCandidates = Array.from(
				doc.querySelectorAll('[data-acc-text="PREV"], [data-acc-text="Prev"], [data-acc-text="prev"], [aria-label*="PREV" i], [aria-label*="Previous" i]')
			).length;
			this.logPrevCrossChallengeDebug('player PREV not found in iframe — bridge not attached', { prevCandidates });
			return;
		}
		const target = this.resolveStoryPlayerPrevBridgeTarget(prevEl);
		if (target.dataset.paraPrevBridged === 'true') {
			return;
		}
		target.dataset.paraPrevBridged = 'true';
		this.logPrevCrossChallengeDebug('attached PREV cross-challenge bridge', {
			prevEnabled: this.isStoryPlayerPrevEnabledForNavigation(prevEl),
			bridgeTarget: target.tagName,
		});
		const handler = (event: Event) => {
			const prevEnabled = this.isStoryPlayerPrevEnabledForNavigation(prevEl);
			const onFirstSlide = this.isOnFirstSlideOfChallenge();
			const shouldIntercept = this.shouldInterceptPrevForPreviousChallenge(prevEl);
			this.logPrevCrossChallengeDebug(`player PREV ${event.type}`, {
				prevEnabled,
				onFirstSlide,
				shouldIntercept,
			});
			if (!shouldIntercept) {
				return;
			}
			this.logPrevCrossChallengeDebug('INTERCEPT — loading previous catalog challenge');
			event.preventDefault();
			event.stopImmediatePropagation();
			this.ngZone.run(() => this.goToPreviousChallenge());
		};
		target.addEventListener('pointerdown', handler, true);
		target.addEventListener('click', handler, true);
	}

	private hideStoryHiddenAccTextElements(doc: Document): void {
		for (const accText of LmsAssessmentSlideoutComponent.STORY_HIDDEN_ACC_TEXTS) {
			doc.querySelectorAll(`[data-acc-text="${accText}"]`).forEach((el) => {
				const node = el as HTMLElement;
				node.style.setProperty('display', 'none', 'important');
				node.style.setProperty('visibility', 'hidden', 'important');
				node.style.setProperty('pointer-events', 'none', 'important');
			});
		}
	}

	/** Auto-select "Start over" when Storyline PlayerMemory shows the resume prompt. */
	private dismissStoryResumePrompt(doc: Document): void {
		const prompt = doc.querySelector('.cs-ResumePromptSlide');
		if (!prompt) {
			return;
		}
		const startOverBtn =
			prompt.querySelector('[class*="ResumePromptSlide-btn1"]') ??
			Array.from(prompt.querySelectorAll('[role="button"], [tabindex="0"], button, [data-acc-text]')).find((el) => {
				const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.toLowerCase();
				return label.includes('start over') || label.includes('start from') || label.includes('no');
			});
		if (startOverBtn instanceof HTMLElement) {
			startOverBtn.click();
		}
	}

	private isStoryScoreButton(el: Element): boolean {
		if (!this.isStoryControlVisible(el) || this.isStoryPlayerChrome(el)) {
			return false;
		}
		const rect = el.getBoundingClientRect();
		// Ignore slide counters and other tiny acc-text labels (e.g. corner "1").
		return rect.width >= 36 && rect.height >= 36;
	}

	private isStoryPlayerChrome(el: Element): boolean {
		const node = el as HTMLElement;
		if (
			node.closest(
				'#bottom_bar, #bottom-bar, #nav-controls, .slide-bottom, .cs-bottombar, .cs-panel, [class*="bottombar"], [class*="player-tab"]'
			)
		) {
			return true;
		}
		const doc = node.ownerDocument;
		if (!doc) {
			return false;
		}
		const rect = node.getBoundingClientRect();
		const viewH = doc.documentElement.clientHeight || doc.body?.clientHeight || 0;
		return viewH > 0 && rect.top > viewH - 56;
	}

	private isStoryControlVisible(el: Element): boolean {
		const node = el as HTMLElement;
		const rects = node.getClientRects();
		if (rects.length === 0) {
			return false;
		}
		const rect = rects[0];
		if (rect.width <= 0 || rect.height <= 0) {
			return false;
		}
		const win = node.ownerDocument?.defaultView;
		if (!win) {
			return false;
		}
		const style = win.getComputedStyle(node);
		if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) <= 0.05) {
			return false;
		}
		const docEl = node.ownerDocument?.documentElement;
		if (!docEl) {
			return false;
		}
		const viewW = docEl.clientWidth;
		const viewH = docEl.clientHeight;
		if (rect.bottom < 4 || rect.right < 4 || rect.top > viewH - 4 || rect.left > viewW - 4) {
			return false;
		}
		return true;
	}

	public scoreFromStoryLine(score: number): void {
		if (this.isLocked) return;
		this.confirmScore = score;
		this.ngZone.run(() => this.persistCurrentScoreAndAdvance());
	}

	returnToScreener(): void {
		this.paraSelectedAssessment.select('para-ce-screener');
		this.closeDrawer();
	}

	dismissScoreBar(): void {
		this.drawerState = 'assessing';
		this.cdr.markForCheck();
	}

	goBackFromConfirmation(): void {
		this.drawerState = 'assessing';
		this.cdr.markForCheck();
	}

	goToConfirmation(): void {
		this.drawerState = 'confirmation';
		this.cdr.markForCheck();
	}

	toggleDateSelector(): void {
		this.showDateSelector = !this.showDateSelector;
	}

	deleteScore(storageKey: string): void {
		this.scoreStorageService.removeScore(storageKey);
		if (this.scoreStorageService.count === 0) {
			this.drawerState = 'assessing';
		}
		this.cdr.markForCheck();
	}

	storeScore(): void {
		this.persistCurrentScoreAndAdvance();
	}

	/** Save the current Storyline score, load the next challenge, or submit when the catalog is finished. */
	private persistCurrentScoreAndAdvance(): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const payload = {
			participantId: this.activeStudent.id,
			testSystemId: 1,
			assessmentBookId: q?.assessmentBookId ?? this.activeStudent.assessmentBookId,
			assessmentTemplateId: this.activeStudent.assessmentTemplateId,
			question: q,
			score: this.confirmScore,
			date: this.selectedDate,
		};

		const nextQuestion = getNextChallengeQuestionAfter(
			q,
			this.questions,
			this.activeStudent?.assessmentBookId,
			this.activeStudent?.assessmentTemplateId
		);
		if (nextQuestion) {
			this.activeStudent.challange.nextChallenge = nextQuestion;
		} else if (this.activeStudent.challange) {
			this.activeStudent.challange.nextChallenge = null;
		}
		this.scoreStorageService.setData(payload);

		if (nextQuestion) {
			this.drawerState = 'assessing';
			this.loadStoryIframe(this.activeStudent);
			this.cdr.markForCheck();
			return;
		}

		this.drawerState = 'assessing';
		if (this.isScreenerLmsAssessment()) {
			this.goToConfirmation();
			return;
		}

		this.cdr.markForCheck();
	}

	setConfirm(): void {
		const rawQ = this.getCurrentChallenge();
		const q = normalizeNextChallengeForLms(rawQ, this.questions) ?? rawQ;
		const payload = {
			participantId: this.activeStudent.id,
			testSystemId: 1,
			assessmentBookId: q?.assessmentBookId ?? this.activeStudent.assessmentBookId,
			assessmentTemplateId: this.activeStudent.assessmentTemplateId,
			question: q,
			score: this.confirmScore,
			date: this.selectedDate,
		};

		this.scoreStorageService.setData(payload);
		this.drawerState = 'confirmation';
		this.cdr.markForCheck();
	}

	submitScore(): void {
		if (this.isSubmitting) return;
		const rawAssessmentId = this.assessmentId ?? this.currentAssessmentId();
		const assessmentId = rawAssessmentId != null ? String(rawAssessmentId).trim() : '';
		if (!assessmentId) {
			console.error('[PARA LMS] Cannot submit scores: missing assessmentId (select an assessment or reopen the drawer).');
			return;
		}
		this.isSubmitting = true;
		this.drawerState = 'submitting';
		this.cdr.markForCheck();
		const nodeId = this.assessmentContextNodeId ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
		this.paraScoreSubmit
			.submitScores({
				scores: this.scoreStorageService.storedScores,
				nodeId,
				assessmentId,
			})
			.subscribe({
				next: (response) => {
					const reportNodeId = this.assessmentContextNodeId ?? normalizeNodeId(this.paraOrgState.getSelectedClassroomSnapshot()?.id);
					if (reportNodeId != null) {
						this.paraProgressReport.refreshProgressReport(reportNodeId);
						// Refresh assessment series (unlock state can change after submit).
						this.paraSeries.refreshAssessmentSeries(reportNodeId);
					}
					// Enrich challenge payload so badge/context can update immediately.
					// Server returns highestChallenge without phase, but currentChallenge/nextChallenge include phase metadata.
					const challenges = response?.challenges;
					if (challenges && typeof challenges === 'object') {
						const hi = challenges.highestChallenge;
						const cur = challenges.currentChallenge;
						if (hi && typeof hi === 'object' && (hi as any).phase == null && cur && typeof cur === 'object') {
							// If currentChallenge matches the submitted/highest question, copy its phase onto highestChallenge.
							const hiQid = Number((hi as any).questionId);
							const curId = Number((cur as any).id);
							if (Number.isFinite(hiQid) && Number.isFinite(curId) && hiQid === curId) {
								(hi as any).phase = (cur as any).phase ?? null;
							}
						}
						// Keep active student + cached roster in sync for immediate UI updates.
						if (this.activeStudent) {
							this.activeStudent.challange = { ...(this.activeStudent.challange || {}), ...challenges };
						}
						if (this.students?.length && this.activeStudent?.id != null) {
							const updatedStudent = this.students.find((s) => String(s.id) === String(this.activeStudent.id));
							if (updatedStudent) {
								updatedStudent.challange = { ...(updatedStudent.challange || {}), ...challenges };
							}
						}
						// Update drawer context (so badge colors + highestChallenge are always "on hand").
						if (this.lmsContext) {
							this.lmsContext = { ...this.lmsContext, highestChallenge: hi ?? this.lmsContext.highestChallenge };
						}
					}
					this.isSubmitting = false;
					const updatedStudent = this.students?.find((student) => student.id === this.activeStudent.id);
					if (updatedStudent && response?.challenges) {
						const element = document.querySelector(`[data-student-id="${updatedStudent.id}"]`);
						if (element) {
							element.classList.add('number-up-animation');
							setTimeout(() => element.classList.remove('number-up-animation'), 1000);
						}
					}

					const finishAfterSubmit = () => {
						this.scoreStorageService.clearData();
						this.drawerState = 'assessing';
						this.closeDrawer();
					};

					this.drawerState = 'done';
					this.cdr.markForCheck();
					setTimeout(() => {
						finishAfterSubmit();
					}, 500);
				},
				error: (err: any) => {
					// Prefer a helpful server message (e.g., "No valid scores to submit.")
					const msg = err?.error?.message ?? err?.error ?? err?.message ?? 'Submit failed. Fix any issues and try again.';
					console.error('[PARA LMS] Submit failed.', msg, err);
					this.isSubmitting = false;
					this.drawerState = 'confirmation';
					this.cdr.markForCheck();
				},
			});
	}
}
